import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Layout from "../components/public/Layout";
import DateTimePage from "../components/public/DateTimePage";
import ConfirmPage from "../components/public/ConfirmPage";
import styles from "../styles/public/booking-page.module.css";
import '@fontsource/poppins'; // Defaults to weight 400
import api from "../services/api";
import ConfirmSelection from "../components/public/ConfirmSelection";
import { Container, Row, Alert } from "reactstrap";

const SwitchBookingPage = () => {
    const { guid } = useParams();
    const [organization, setOrganization] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [originalStart, setOriginalStart] = useState(null);
    const [rescheduleError, setRescheduleError] = useState('');
    const [bookingData, setBookingData] = useState({
        service: {},
        date: null,
        time: null,
        customer_name: '',
        contactInfo: null,
        organization: null,
        eventId: null,
        eventGuid: null,
        eventStart: null,
        eventEnd: null,
    });

    useEffect(() => {
        document.title = 'Reserve2Day';

        setCurrentStep(1);
    }, []); // <-- This should be outside the .then()

    useEffect(() => {
        // Fetch the booking data
        api.get(`/get-booking/?event=${guid}`, {
            skipAuthHeader: true,
            skipAuthRedirect: true,
        })
            .then((response) => {
                setOrganization(response.data.organizationDetails);
                setOriginalStart(response.data.start);
                const start = new Date(response.data.start);
                const time = start.toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                });
                start.setHours(0, 0, 0, 0);
                // const date = start.toISOString().split("T")[0]; // YYYY-MM-DD

                const leagueDate = new Date(response.data.league_date.date);
                const maxDate = response.data.league_date.max_date 
                    ? new Date(new Date(response.data.league_date.max_date).setDate(new Date(response.data.league_date.max_date).getDate() + 1))
                    : new Date(new Date(leagueDate).setDate(new Date(leagueDate).getDate() + 7));
                setBookingData((prev) => ({
                    ...prev,
                    service: response.data.serviceDetails,
                    organization: response.data.organization,
                    customer_name: response.data.customer_name,
                    date: start,
                    time: time,
                    maxDate: maxDate,
                }));
            })
            .catch((err) => {
                console.error(err);
            });
    }, [guid]);

    const updateBookingData = (data) => {
        setBookingData((prev) => ({ ...prev, ...data }));
    };

    const updateEvent = async (time) => {
        try {
            const startDate = new Date(bookingData.date);

            const [timePart, amPm] = time.split(' ');
            const [hours, minutes] = timePart.split(':').map(Number);

            let startHours;
            if (amPm.toLowerCase() === 'pm' && hours !== 12) {
                startHours = hours + 12;
            } else if (amPm.toLowerCase() === 'am' && hours === 12) {
                startHours = 0;
            } else {
                startHours = hours;
            }

            startDate.setHours(startHours);
            startDate.setMinutes(minutes);
            startDate.setSeconds(0);

            const durationInMinutes = bookingData.service.duration;
            const durationInHours = Math.floor(durationInMinutes / 60);

            const endDate = new Date(startDate);
            endDate.setHours(endDate.getHours() + durationInHours);

            await api.put(
                `/book/${guid}/`,
                {
                    start: startDate.toISOString(),
                    end: endDate.toISOString(),
                    resourceLocked: false,
                    sendEmail: true,
                    emailType: "league",
                },
                {
                    skipAuthHeader: true,
                    skipAuthRedirect: true,
                }
            );

            return true;
        } catch (error) {
            console.log("Error updating event:", error);
            if (error.response?.data) {
                const errorMsg = error.response.data.detail || 'An error occurred while rescheduling your booking';
                setRescheduleError(errorMsg);
                setCurrentStep(1);
            }
            return false;
        }
    };

    const handleTimeSelected = () => {
        setCurrentStep(2);
    }

    const handleCancel = () => {
        setCurrentStep(1);
    };

    const handleConfirm = () => {
        try {
            updateEvent(bookingData.time);

            setCurrentStep(3);
        } catch (error) {
            console.log(error.message);
        }
    };

    const renderOriginalBooking = () => {
        return (
            <Container>
                <Row className="text-center justify-content-center p-2">
                    <strong>Reschedule {new Date(originalStart).toLocaleString("en-us", {
                        month: "long",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                    })} booking for {bookingData.customer_name}
                    </strong>
                </Row>
            </Container>
        );
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <>
                        {renderOriginalBooking()}
                        <DateTimePage
                            bookingData={bookingData}
                            updateBookingData={updateBookingData}
                            onTimeSelected={handleTimeSelected}
                            initialDate={bookingData.date || new Date()}
                            maxDate={bookingData.maxDate}
                            displayPrices={false}
                            editableSelections={false}
                            organization={organization}
                        />
                    </>
                );
            case 2:
                return (
                    <>
                        {renderOriginalBooking()}
                        <ConfirmSelection
                            bookingData={bookingData}
                            onConfirm={handleConfirm}
                            onCancel={handleCancel}
                        />
                    </>
                );
            case 3:
                return (
                    <ConfirmPage
                        bookingData={bookingData}
                        organization={organization}
                        showCancel={false}
                        showExpectations={false}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Layout
            organization={organization}
            showNavigation={false}
            navigationProps={{
                onClick: null,
                handleContinue: null,
                disabled: true,
                continueText: "Continue",
                showContinueButton: currentStep !== 2
            }}
        >
            <div className={styles["booking-page"]}>
                {rescheduleError && (
                    <Container>
                        <Alert color="danger" className="mt-3 text-center" toggle={() => setRescheduleError('')} fade={false}>
                            {rescheduleError}
                        </Alert>
                    </Container>
                )}
                {renderStep()}
            </div>
        </Layout>
    );
};

export default SwitchBookingPage;
